/**
 * 🎨 HeroAvatar — the user's Nano-Banana Phoenix Hero portrait.
 *
 * Drop-in <HeroAvatar userId size> component that:
 *  - Fetches /api/hero/portrait/of/{userId} (or /api/hero/portrait for self)
 *  - Renders the portrait circular with the royal-purple ring
 *  - Falls back to a default silhouette when the user hasn't generated yet
 *
 * Reusable across Arena fighter cards, chat bubbles, profile headers,
 * leaderboards, and Miko's Arcade hero-select.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small in-memory cache so 20 chat bubbles don't hammer the API.
const _cache = new Map();     // userId → { dataUrl, ts }
const CACHE_MS = 5 * 60_000;  // 5 min

async function fetchPortrait(userId, self = false) {
  const cacheKey = self ? "__self__" : userId;
  const hit = _cache.get(cacheKey);
  if (hit && Date.now() - hit.ts < CACHE_MS) return hit.dataUrl;
  try {
    const url = self
      ? `${API}/hero/portrait`
      : `${API}/hero/portrait/of/${encodeURIComponent(userId)}`;
    const { data } = await axios.get(url);
    const dataUrl = data?.portrait?.data_url || null;
    _cache.set(cacheKey, { dataUrl, ts: Date.now() });
    return dataUrl;
  } catch {
    _cache.set(cacheKey, { dataUrl: null, ts: Date.now() });
    return null;
  }
}

/** Manually blow the cache after a regen. */
export function invalidateAvatarCache(userId) {
  if (userId) _cache.delete(userId);
  _cache.delete("__self__");
}

export default function HeroAvatar({
  userId,        // optional — omit for self
  self = false,
  size = 48,     // px
  ring = true,   // royal-purple ring
  className = "",
  fallback = null,
  onClick,
  testid,
}) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      const s = await fetchPortrait(userId, self || !userId);
      if (mounted) setSrc(s);
    })();
    return () => { mounted = false; };
  }, [userId, self]);

  const base = ring
    ? "rounded-full border-2 border-[#6D28D9] shadow-[0_0_10px_rgba(109,40,217,0.35)]"
    : "rounded-full";
  const style = { width: size, height: size };

  if (src) {
    return (
      <img
        src={src}
        alt="Hero portrait"
        className={`${base} object-cover ${onClick ? "cursor-pointer" : ""} ${className}`}
        style={style}
        onClick={onClick}
        data-testid={testid || "hero-avatar"}
      />
    );
  }
  // Fallback: circular silhouette with the medallion tint.
  return (
    <div
      className={`${base} bg-gradient-to-br from-[#4C1D95] to-[#1E1B4B] flex items-center justify-center text-white/70 text-lg ${onClick ? "cursor-pointer" : ""} ${className}`}
      style={style}
      onClick={onClick}
      data-testid={testid || "hero-avatar-fallback"}
    >
      {fallback ?? "🐦‍🔥"}
    </div>
  );
}
